<script lang="ts" setup>
import type { FilterArrayKeys, ISearchState, SearchFilterOption } from '@/components/Search/types.ts'
import { useI18n } from '@/plugins/i18n'
import DocItem from '../Common/DocItem.ce.vue';
import FilterDropdown from '@/components/Search/FilterDropdown.ce.vue';
import SelectedFiltersHeader from '../Common/SelectedFiltersHeader/index.ce.vue'
import iconMain from '@/assets/img/icon-search-result-sort.svg';
import iconOpen from '@/assets/img/icon-search-result-sort-close.svg';
import iconClose from '@/assets/img/icon-search-result-sort-close.svg';
import { useSearchStore } from '@/components/Search/store/index.ts'
import { useSearchActions } from '@/components/Search/SearchResults/Composables/useSearchActions'
import SaiDropDown from '@/components/common/SaiDropDown/index.ce.vue';
import SaiPagination from '@/components/common/SaiPagination/index.ce.vue';
import { STANDARDS_SEARCH_TAB, OPENSEARCH_RESULT_WINDOW, STANDARDS_RECORDS_PER_PAGE, STANDARDS_FILTER_KEYS } from '@/components/Search/constants.ts';
import InitialEmptyResultView from '@/components/Search/SearchResults/Common/InitialEmptyResultView/index.ce.vue';
import SaveSearch from '@/components/Search/SearchResults/Common/SaveSearch/index.ce.vue';
import ExportSearch from '@/components/Search/SearchResults/Common/ExportSearch/index.ce.vue';
import NoResult from '../Common/NoResult/index.ce.vue';

//store
const searchStore = useSearchStore()

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

const { 
  isAllSelected,
  handleSelectItem,
  handleDocAction,
  handleSelectAllItem,
  handleBulkDocAction,
  removeFilter,
  clearAllFilters,
  saveFilters,
  getResultActionOptions,
  getResultsRecordTotal,
  getResultSummaryText,
  isResultLoaded, 
  getDocOffset,
  handleExportFieldSelection,
  handleExportFormatSelection,
  handleExportSearchResults,
  handleExportClose,
  handleProductLinkClick
} = useSearchActions(STANDARDS_SEARCH_TAB, STANDARDS_FILTER_KEYS)

const getRecordsPerPage = () => {
  return Number(searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.recordsPerPage) || STANDARDS_RECORDS_PER_PAGE;
};

const getMaxAllowedPage = () => {
  const recordsPerPage = getRecordsPerPage();
  if (recordsPerPage <= 0) return 1;
  return Math.max(1, Math.floor(OPENSEARCH_RESULT_WINDOW / recordsPerPage));
};

const gotToPage = (pageNum: number) => {
  const safePage = Math.min(Math.max(pageNum, 1), getMaxAllowedPage());
  searchStore.searchStandards(true, safePage);
};

const arrStandardSearchTips = [
  'noResultStandardSearchTip1',
  'noResultStandardSearchTip2',
  'noResultStandardSearchTip3',
  'noResultStandardSearchTip4',
  'noResultStandardSearchTip5',
  'noResultStandardSearchTip6',
  'noResultStandardSearchTip7'
];


 const exportFieldOptions: {key: string, val: string}[] = [
    {key: 'docId', val: t('docId')},
    {key: 'name', val: t('name')},
    {key: 'title', val: t('title')},
    {key: 'status', val: t('status')},
    {key: 'language', val: t('language')},
    {key: 'publisher', val: t('publisher')},
    {key: 'country', val: t('country')},
    {key: 'latestVersion', val: t('latestVersion')},
    {key: 'versionDate', val: t('versionDate')},
    {key: 'summary', val: t('summary')},
];

</script>

<template>
  <InitialEmptyResultView  v-if="!isResultLoaded && !searchStore[`${STANDARDS_SEARCH_TAB}.result.loading`]" 
    :title="t('findAStandard')" :subTitle ="t('typeInSearchToGetStarted')"></InitialEmptyResultView>
  <div v-if="isResultLoaded && getResultsRecordTotal() === 0" class="search-result search-result--noresult">
    <template v-if="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.nohits">
      <NoResult :searchTerms="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.searchTerms" :searchTips="arrStandardSearchTips"/>
    </template>
  </div>
  <div v-if="isResultLoaded && getResultsRecordTotal() > 0" class="search-result">
    <SelectedFiltersHeader :filterKeys="STANDARDS_FILTER_KEYS" @removeFilter="removeFilter" @clearAll="clearAllFilters" @saveFilters="saveFilters"/>
    <div class="search-result--summary-for-mobile">
      <div v-if="getResultSummaryText()" class="search-results-res">{{t('resultsFor', [getResultsRecordTotal().toString(), getResultSummaryText()])}}</div>
      <div v-else class="search-results-res">{{getResultsRecordTotal().toString()}} {{ " " + t('results') }}</div>
    </div>
    <div class="search-result--header">
      <div v-if="getResultActionOptions().length" class="search-result--header-left">
        <div class="doc-action--select-all">
          <img v-if="!isAllSelected" src="@/assets/img/icon-checkbox.svg" @click="handleSelectAllItem"/>
          <img v-if="isAllSelected" src="@/assets/img/icon-checkbox-active.svg" @click="handleSelectAllItem"/>
          {{ t('batchAction') }}
        </div>
        <div class="doc-action">
          <SaiDropDown 
            className="dropdown bulk-action"
            placeholder="Action"
            :multiple="false"
            name="standards-select-all"
            :hasClear="false"
            :options="getResultActionOptions()"
            :iconOpen="iconClose"
            :iconClose="iconClose"
            :closeOnSelect="true"
            @updateSelectedItems="handleBulkDocAction"
            :showSelectCountInHeader="false"
            :closeOnClickOutside="true"
          />
        </div>
      </div>
      <div class="search-result--header-center">
        <div v-if="getResultSummaryText()" class="search-results-res">{{t('resultsFor', [getResultsRecordTotal().toString(), getResultSummaryText()])}}</div>
        <div v-else class="search-results-res">{{getResultsRecordTotal().toString()}} {{ " " + t('results') }}</div>
      </div>
      <div class="search-result--header-right">
          <FilterDropdown
            className="dropdown result-sort" 
            :placeholder="t('sort')" 
            :placeholderPrefix="`${t('sort')}:`" 
            name="standards_sort" 
            :multiple="false"
            field="standards.result.sort"
            lookupField="lookUpOptions.standards.sortOptions"
            :translate="true"
            :hasClear="false" 
            :iconMain="iconMain"
            :iconOpenMain="iconMain"
            :iconOpen="iconOpen"
            :iconClose="iconClose"
            :searchable="false"
            :showMoreLink="false"
            :closeOnClickOutside="true"
            :showSelectCountInHeader="false"
            :closeOnSelect="true"
            :showSelectedInPlaceholder="true"
            @updateSelectedItems="() => searchStore.searchStandards(true)"
            />
            <SaveSearch :searchType="STANDARDS_SEARCH_TAB" :saveForMonthlyAlerts="true"></SaveSearch>
            <ExportSearch 
              :name="STANDARDS_SEARCH_TAB"
              :fieldOptions="exportFieldOptions"
              :status="searchStore[`${STANDARDS_SEARCH_TAB}.export.status`]"
              :selectedFormat="searchStore[`${STANDARDS_SEARCH_TAB}.export.format`]"
              :totalFound="searchStore[`${STANDARDS_SEARCH_TAB}.export.totalFound`] ?? 0"
              :selectedFields="searchStore[`${STANDARDS_SEARCH_TAB}.export.selectedFields`] ?? []"
              :searchContext="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.searchContext"
              :note="t('exportNoteStandards')"
              @handleExportFieldSelection="handleExportFieldSelection"
              @handleExportFormatSelection="handleExportFormatSelection"
              @handleExportSearchResults="handleExportSearchResults"
              @handleExportClose="handleExportClose">
            </ExportSearch>
      </div>
    </div>
    <div class="search-result-items">
      <DocItem v-for="(record, index) in searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.records" 
        :docOffset="getDocOffset(index)"
        :item="record" 
        :searchContext="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.searchContext" 
        :activeCollectionName="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.activeCollectionName ?? ''"
        @handleDocAction="handleDocAction"
        @handleSelectItem="handleSelectItem"
        @handleProductLinkClick="handleProductLinkClick(record, index)"
        :selectedItems="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.selectedItems ?? []"
        :key="index"/>
    </div>
    <div class="search-result--footer">
      <SaiPagination v-if="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.hits ?? 0 > 0"
          :current-page="searchStore[`${STANDARDS_SEARCH_TAB}.result.page`] "
          :total-items="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.hits ?? 0"
          :page-size="searchStore[`${STANDARDS_SEARCH_TAB}.resultData`]?.recordsPerPage ?? 0"
          :max-result-window="OPENSEARCH_RESULT_WINDOW"
          @goToPage="gotToPage"></SaiPagination>
    </div>
  </div>
</template>
<style lang="scss">
    @use './style.scss';
</style>
